import { useState, useRef, useEffect, ReactNode } from 'react';
import './Dropdown.css';

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
}

/**
 * Reusable dropdown component with click-outside detection
 * @param trigger - The element that triggers the dropdown when clicked
 * @param children - The content to display inside the dropdown
 * @param align - Alignment of the dropdown menu (left or right)
 * @returns The rendered dropdown component
 */
function Dropdown({ trigger, children, align = 'right' }: DropdownProps): JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Handles click outside to close the dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Toggles the dropdown open/closed state
   */
  const toggleDropdown = (): void => {
    setIsOpen(!isOpen);
  };

  /**
   * Closes the dropdown
   */
  const closeDropdown = (): void => {
    setIsOpen(false);
  };

  return (
    <div className="dropdown" ref={dropdownRef}>
      <div className="dropdown-trigger" onClick={toggleDropdown}>
        {trigger}
      </div>
      {isOpen && (
        <div className={`dropdown-menu dropdown-menu--${align}`} onClick={closeDropdown}>
          {children}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
